import { Form, FormField, FormExtendedEvent, Button, Text, Card, CardHeader, CardBody, Paragraph, Box, TextInput } from 'grommet'
import { CircleQuestion } from 'grommet-icons'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { TextWithLoader, breakpoints } from '@mindthecoin/shiny'

import CountryDropdown, { Country } from '../../ui/CountryDropdown'
import BusinessAreaDropdown from '../../ui/BusinessAreaDropdown'
import BusinessRevenueDropdown from '../../ui/BusinessRevenue'
import HighRiskCountryDropdown from '../../ui/HighRiskCountryDropdown'

import { useSendAdditionalInformation, maxStringLength, AdditionalInformation } from '../../../api/additionalInformation'
import { useGoToPage } from '../../../routeConfig'
import { useStatus, VerificationStatus } from '../../../api/status'
import LoadingSpinner from '../../ui/LoadingSpinner'
import { useAdditionalStringValidators, useSecondsCountdown, isString } from './utils'
import Tooltip from '../../ui/Tooltip'

type FormData = {
  highRiskCountry: Country[],
  businessRevenue: string,
  businessArea: string,
  placeOfBirth: string,
  occupation?: string,
  employer: string,
  otherNationalities?: Country[],
  relationshipPurpose?: string,
  // TODO: can we make this data structure nested while using Grommet?
  'nonFiscalAddress.line1'?: string,
  'nonFiscalAddress.line2'?: string,
  'nonFiscalAddress.postalCode'?: string,
  'nonFiscalAddress.city'?: string,
  'nonFiscalAddress.country'?: Country
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 0;

  @media (min-width: ${breakpoints.large.value}px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    flex-wrap: wrap;

    & > * {
      width: 25rem;
    }

    & > *:first-child {
      margin-right: 2rem;
      margin-top: 2.5rem;
    }
  }
`

function AdditionalInformationForm() {
  const intl = useIntl()
  const goToPage = useGoToPage()
  const stringValidators = useAdditionalStringValidators()
  const { countdown: redirectCountdown, startCountdown, stopCountdown } = useSecondsCountdown()
  const { data: statusInfo, isLoading: statusInfoLoading, isFetched: statusInfoFetched } = useStatus()

  const { mutate: sendAdditionalInformation, isLoading: isSubmittingForm, isError, isSuccess } = useSendAdditionalInformation({
    onSuccess: () => {
      startCountdown(10)
    }
  })

  const [formData, setFormData] = useState<FormData>({
    highRiskCountry: [],
    businessRevenue: '',
    businessArea: '',
    placeOfBirth: '',
    employer: '',
  })

  useEffect(() => {
    if (statusInfoFetched && statusInfo?.status !== VerificationStatus.missingInformation) {
      // Go back to the status page if the user has already submitted the required information
      goToPage('status')
    }
  }, [statusInfoFetched, statusInfo?.status, goToPage])

  useEffect((): void => {
    if (isSuccess && redirectCountdown === 1) {
      goToPage('status')
    }
  }, [isSuccess, redirectCountdown, goToPage, stopCountdown])

  const onChange = (nextValue: FormData) => setFormData(nextValue)

  const onSubmit = ({ value }: FormExtendedEvent<FormData>) => {
    const nonFiscalAddress: Array<string> = [
      value['nonFiscalAddress.line1'],
      value['nonFiscalAddress.line2'],
      value['nonFiscalAddress.postalCode'],
      value['nonFiscalAddress.city'],
      value['nonFiscalAddress.country']?.countryCode,
    ].filter(isString)

    const info: AdditionalInformation = {
      occupation: value.occupation,
      employer: value.employer,
      relationshipPurpose: value.relationshipPurpose,
      placeOfBirth: value.placeOfBirth,
      otherNationalities: value.otherNationalities?.map(country => country.countryCode),
      nonFiscalAddress: nonFiscalAddress,
      businessArea: value.businessArea,
      businessRevenue: value.businessRevenue,
      businessHighRiskCountries: value.highRiskCountry?.map(country => country.countryCode),
    }

    sendAdditionalInformation(info)
  }

  if (statusInfoLoading) {
    return <LoadingSpinner />
  }

  return (
    <Form
      messages={{
        required: intl.formatMessage({ id: 'form.fields.mandatory' }),
      }}
      validate='blur'
      value={formData}
      onChange={onChange}
      onSubmit={onSubmit}
    >
      <Box>
        <FormContainer>
          <div>
            <FormField
              name="placeOfBirth"
              required={true}
              help={intl.formatMessage({ id: 'moreInfo.fields.placeOfBirth.help' })}
              maxLength={maxStringLength}
              margin={{ bottom: "medium" }}
              label={intl.formatMessage({ id: 'moreInfo.fields.placeOfBirth' })}
              validate={stringValidators('moreInfo.fields.placeOfBirth', 3, 20)}
            >
              <TextInput name="placeOfBirth" size="medium" />
            </FormField>

            <FormField
              name="otherNationalities"
              margin={{ bottom: "medium" }}
              help={intl.formatMessage({ id: 'moreInfo.fields.otherNationalities.help' })}
              label={intl.formatMessage({ id: 'moreInfo.fields.otherNationalities' })}
            >
              <CountryDropdown name="otherNationalities" multiple />
            </FormField>

            <FormField
              required={true}
              name="occupation"
              margin={{ bottom: "medium" }}
              maxLength={maxStringLength}
              help={intl.formatMessage({ id: 'moreInfo.fields.occupation.help' })}
              label={intl.formatMessage({ id: 'moreInfo.fields.occupation' })}
              validate={stringValidators('moreInfo.fields.occupation', 5, 50)}
            >
              <TextInput name="occupation" size="medium" />
            </FormField>

            <FormField
              required={true}
              name="employer"
              maxLength={maxStringLength}
              help={intl.formatMessage({ id: 'moreInfo.fields.employer.help' })}
              label={intl.formatMessage({ id: 'moreInfo.fields.employer' })}
              validate={stringValidators('moreInfo.fields.employer')}
            >
              <TextInput name="employer" size="medium" />
            </FormField>

            <FormField
              required={true}
              name="businessArea"
              margin={{ bottom: "medium" }}
              help={intl.formatMessage({ id: 'moreInfo.fields.businessArea.help' })}
              label={intl.formatMessage({ id: 'moreInfo.fields.businessArea' })}
            >
              <BusinessAreaDropdown name="businessArea" />
            </FormField>

            <FormField
              required={true}
              name="businessRevenue"
              margin={{ bottom: "medium" }}
              help={intl.formatMessage({ id: 'moreInfo.fields.businessRevenue.help' })}
              label={intl.formatMessage({ id: 'moreInfo.fields.businessRevenue' })}
            >
              <BusinessRevenueDropdown name="businessRevenue" />
            </FormField>

            <FormField
              name="highRiskCountry"
              margin={{ bottom: "medium" }}
              label={intl.formatMessage({ id: 'moreInfo.fields.highRiskCountry' })}
              help={intl.formatMessage({ id: 'moreInfo.fields.highRiskCountry.help' })}
            >
              <HighRiskCountryDropdown name="highRiskCountry" multiple />
            </FormField>

            <FormField
              required={true}
              name="relationshipPurpose"
              maxLength={maxStringLength}
              label={intl.formatMessage({ id: 'moreInfo.fields.relationshipPurpose' })}
              help={intl.formatMessage({ id: 'moreInfo.fields.relationshipPurpose.help' })}
              validate={stringValidators('moreInfo.fields.relationshipPurpose', 5, 80)}
            >
              <TextInput name="relationshipPurpose" size="medium" />
            </FormField>

          </div>

          <div>
            <Card margin={{ top: 'medium', bottom: 'medium' }} border={{ color: 'light-6' }} elevation='xsmall'>
              <CardHeader pad={{ left: 'medium', right: 'medium', top: 'medium', bottom: 'small' }} justify="between" align="middle">
                <Text size="medium" weight="bold">
                  <FormattedMessage id="moreInfo.fields.address.title" />{' '}
                </Text>

                <Tooltip content={intl.formatMessage({ id: 'moreInfo.fields.address.info' })}>
                  <CircleQuestion color="brand" />
                </Tooltip>
              </CardHeader>

              <CardBody pad={{ left: 'medium', right: 'medium', bottom: 'medium', top: 'small' }}>
                <FormField
                  name="nonFiscalAddress.line1"
                  maxLength={maxStringLength}
                  label={intl.formatMessage({ id: 'moreInfo.fields.address.line1' })}
                  validate={stringValidators('moreInfo.fields.address.line1')}
                >
                  <TextInput name="nonFiscalAddress.line1" size="medium" />
                </FormField>

                <FormField
                  name="nonFiscalAddress.line2"
                  maxLength={maxStringLength}
                  label={intl.formatMessage({ id: 'moreInfo.fields.address.line2' })}
                  validate={stringValidators('moreInfo.fields.address.line2')}
                >
                  <TextInput name="nonFiscalAddress.line2" size="medium" />
                </FormField>

                <FormField
                  name="nonFiscalAddress.postalCode"
                  maxLength={maxStringLength}
                  label={intl.formatMessage({ id: 'moreInfo.fields.address.postalCode' })}
                  validate={stringValidators('moreInfo.fields.address.postalCode')}
                >
                  <TextInput name="nonFiscalAddress.postalCode" size="medium" />
                </FormField>

                <FormField
                  name="nonFiscalAddress.city"
                  maxLength={maxStringLength}
                  label={intl.formatMessage({ id: 'moreInfo.fields.address.city' })}
                  validate={stringValidators('moreInfo.fields.address.city')}
                >
                  <TextInput name="nonFiscalAddress.city" size="medium" />
                </FormField>

                <FormField
                  name="nonFiscalAddress.country"
                  label={intl.formatMessage({ id: 'moreInfo.fields.address.country' })}
                >
                  <CountryDropdown name="nonFiscalAddress.country" />
                </FormField>
              </CardBody>
            </Card>
          </div>
        </FormContainer>

        <Paragraph margin="none" textAlign="center" alignSelf="center">
          <Button size="large" type="submit" alignSelf="center" label={
            <TextWithLoader isLoading={isSubmittingForm}>
              <FormattedMessage id="moreInfo.buttons.submit" />
            </TextWithLoader>
          } primary />
        </Paragraph>

        {(isSuccess || isError) && (
          <Paragraph margin={{ bottom: 'none', top: 'medium' }} textAlign="center" alignSelf="center">
            {isSuccess && <Text color="status-ok" size="large"><FormattedMessage id="moreInfo.status.success" values={{ countdown: redirectCountdown }} /></Text>}
            {isError && <Text color="status-error" size="large"><FormattedMessage id="moreInfo.status.error" /></Text>}
          </Paragraph>
        )}
      </Box>
    </Form>
  )
}

export default AdditionalInformationForm
